<template>
  <article class="event-catalog-placement" v-if="event">
    <h3 class="event-catalog-placement__header">
      {{ $t("event.catalog.title") }}

      <icon
        name="event-catalog-placement-header"
        class="event-catalog-placement__header-icon"
      />
    </h3>

    <template v-if="!hasPrice && !isSentOnPaper">
      <Notification v-if="currentDate.isBefore(startCatalogOrderDate)">
        {{
          $t("event.catalog.startDate", {
            date: startCatalogOrderDate.format("DD MMMM YYYY")
          })
        }}
      </Notification>

      <Notification v-else>
        {{ $t("event.catalog.unavailable") }}
      </Notification>
    </template>

    <template v-else-if="!isSentOnPaper">
      <Notification v-if="currentDate.isBefore(startCatalogOrderDate)">
        {{
          $t("event.catalog.startDate", {
            date: startCatalogOrderDate.format("DD MMMM YYYY")
          })
        }}
      </Notification>

      <Notification
        v-if="
          currentDate.isBetween(
            startCatalogOrderDate,
            endCatalogOrderDatePlus1Day,
            null,
            '[]'
          )
        "
        :warning="endCatalogOrderDate - currentDate <= 1000 * 60 * 60 * 24 * 10"
        modify="_flex _documents"
        icon="warning-border--no-color"
        clickable
      >
        <div>
          <span>
            {{
              $t("event.catalog.deadlineInfo", {
                date: endCatalogOrderDate.format("DD MMMM YYYY")
              })
            }}
          </span>
          <EventModalCatalogExample
            :label="false"
            :images="['/static/catalog-order/catalog-minimum-example.jpg']"
          />
        </div>
      </Notification>

      <Notification
        v-if="
          currentDate.isSameOrAfter(endCatalogOrderDatePlus1Day) && !hasOrder
        "
      >
        {{ $t("event.catalog.deadlinePassed") }}
      </Notification>
    </template>

    <div class="event-catalog-placement__content">
      <template v-if="event.catalog_order_id && !isSentOnPaper">
        <template v-if="hasOrder">
          <div class="event-catalog-placement__finish-application">
            <div class="event-catalog-placement__finish-application-status">
              <DotText
                class="_success"
                v-if="event.catalog_order_status === 'confirmed'"
              >
                {{ $t("event.catalog.orderAccepted") }}
              </DotText>
            </div>

            <LinkIcon
              class="look"
              icon="look"
              @click="$modal.show('event-modal-catalog')"
            >
              {{ $t("common.lookBtn") }}
            </LinkIcon>
          </div>
        </template>

        <template v-else>
          <div class="event-catalog-placement__finish-application">
            <div class="event-catalog-placement__finish-application-status">
              <DotText
                class="_warning"
                v-if="event.catalog_order_status === 'draft'"
              >
                {{ $t("event.catalog.orderNotSent") }}
              </DotText>

              <DotText
                class="_warning"
                v-if="event.catalog_order_status === 'moderation'"
              >
                {{ $t("event.catalog.orderModeration") }}
              </DotText>

              <DotText
                class="_warning"
                v-if="event.catalog_order_status === 'sent'"
              >
                {{ $t("event.catalog.orderSent") }}
              </DotText>
            </div>
            <template
              v-if="
                currentDate.isBetween(
                  startCatalogOrderDate,
                  endCatalogOrderDatePlus1Day,
                  null,
                  '[]'
                )
              "
            >
              <template
                v-if="
                  hasAccessToWrite && event.catalog_order_status === 'draft'
                "
              >
                <LinkIcon
                  icon="link-icon-edit"
                  @click="$modal.show('event-modal-catalog')"
                >
                  {{ $t("common.editBtn") }}
                </LinkIcon>
              </template>

              <template v-else>
                <LinkIcon
                  class="look"
                  icon="look"
                  @click="$modal.show('event-modal-catalog')"
                >
                  {{ $t("common.lookBtn") }}
                </LinkIcon>
              </template>
            </template>
          </div>
        </template>
      </template>

      <template v-else-if="!isSentOnPaper">
        <template
          v-if="
            hasAccessToWrite &&
              hasPrice &&
              currentDate.isBetween(
                startCatalogOrderDate,
                endCatalogOrderDatePlus1Day,
                null,
                '[]'
              )
          "
        >
          <PrimaryButton @click="$modal.show('event-modal-catalog')">
            {{ $t("event.catalog.sendBtn") }}
          </PrimaryButton>
        </template>

        <template
          v-else-if="
            hasPrice && currentDate.isSameOrAfter(startCatalogOrderDate)
          "
        >
          <DotText class="_warning">
            {{ $t("event.catalog.orderNotSent") }}
          </DotText>
        </template>
      </template>

      <template v-if="isSentOnPaper">
        <DotText class="_success">
          {{ $t("event.catalog.hasCatalogOrder1cMessage") }}
        </DotText>
      </template>

      <p class="event-catalog-placement__content-note">
        <a
          :href="
            `/static/docs/general_terms_of_publication_in_the_official_catalogue_guide_${locale}.pdf`
          "
          target="_blank"
        >
          {{ $t("event.catalog.termsPart1") }}
        </a>

        {{ $t("event.catalog.termsPart2") }}
      </p>
    </div>
  </article>
</template>

<script>
import EventModalCatalogExample from "@/components/Modal/Catalog/EventModalCatalogExample.vue";
import {
  PrimaryButton,
  LinkIcon,
  DotText,
  Notification
} from "@v2/shared/ui/components";

import { mapState, mapGetters, mapActions } from "vuex";

import moment from "moment";

import diffInSeconds from "@/helpers/diffInSeconds";
import { i18n } from "@v2/shared/config";

export default {
  name: "EventCatalogPlacement",

  components: {
    Notification,
    PrimaryButton,
    LinkIcon,
    DotText,
    EventModalCatalogExample
  },

  computed: {
    ...mapState("event", ["event"]),
    ...mapState("eventCatalog", {
      info: "info",
      catalogStatus: "status"
    }),
    ...mapGetters("event", ["hasAccessToWrite"]),

    locale() {
      return i18n.locale;
    },

    currentDate() {
      return moment();
    },

    startCatalogOrderDate() {
      return moment(this.info.catalog_order_started_at).locale(i18n.locale);
    },

    endCatalogOrderDate() {
      return moment(this.info.catalog_order_ended_at).locale(this.locale);
    },

    endCatalogOrderDatePlus1Day() {
      return moment(this.info.catalog_order_ended_at).add(1, "day");
    },

    hasPrice() {
      return !!(this.info.services && this.info.services.length);
    },

    hasOrder() {
      return (
        this.event.catalog_order_status &&
        this.event.catalog_order_status === "confirmed"
      );
    },

    isSentOnPaper() {
      return this.event.catalog_order_is_paper_application || false;
    }
  },

  methods: {
    diffInSeconds,
    ...mapActions("event", ["updateCatalogStatuses"])
  },
  watch: {
    catalogStatus(value) {
      this.updateCatalogStatuses(value);
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-catalog-placement {
  display: flex;
  flex-direction: column;
  width: 460px;
  margin-top: 20px;
  box-sizing: border-box;
  position: relative;

  &.services {
    margin-top: 30px;
  }
  @include respond-down(lg) {
    width: 350px;
  }

  @include respond-down(md) {
    margin-top: 0;
  }

  &__header {
    @include h2();

    padding: 20px 30px;

    @include respond-down(md) {
      padding: 15px;
      font-size: 1rem;
    }

    border-bottom: 1px solid var(--gray-darker);

    position: relative;

    &-icon {
      position: absolute;
      right: 19px;
      bottom: 0;

      width: 72px;
      height: 67px;

      @include respond-down(lg) {
        width: 62px;
        height: 57px;

        right: 10px;
      }

      @include respond-down(md) {
        width: 44px;
        height: 47px;
        right: 14px;
      }
    }
  }

  @include respond-down(lg) {
    .notification {
      padding-left: 28px;
      padding-right: 28px;
    }
  }

  &__content {
    // height: 100%;
    flex-grow: 1;

    display: flex;
    flex-direction: column;
    align-items: flex-start;

    margin-top: auto;

    padding: 30px;

    @include respond-down(lg) {
      padding-left: 28px;
      padding-right: 28px;
    }

    @include respond-down(md) {
      padding: 15px;
    }
    & .svg-icon.svg-icon--look {
      width: 20px;
      height: 20px;
    }
    &-note {
      width: 100%;

      margin-top: auto;
      margin-bottom: 0;

      padding-top: 30px;

      font-size: 12px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.5;
      letter-spacing: normal;
      color: #a3a8b3;

      @include respond-down(md) {
        padding-top: 24px;
        font-size: 11px;
      }

      a {
        @include hyperlink();
        font-size: inherit;
        color: inherit;
      }
    }
  }

  &__finish-application {
    display: flex;
    justify-content: space-between;
    align-items: center;

    width: 100%;

    margin-top: 10px;
    margin-bottom: 12px;

    &-status {
      margin-top: 1px;
      margin-right: 5px;
    }
  }

  &__footer {
    padding: 23px 0 24px 40px;
    border-top: 1px solid rgba(205, 206, 208, 0.5);
    &-text {
      @include description();
      display: flex;
      align-items: center;

      .svg-icon {
        width: 16px;
        height: 16px;
        margin-right: 8px;
      }
    }
  }
}
</style>
